"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutPropertiesLoose2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutPropertiesLoose"));
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _core = require("@material-ui/core");
var _useMediaQuery = _interopRequireDefault(require("@material-ui/core/useMediaQuery"));
var _styles = require("@material-ui/core/styles");
var _clsx = _interopRequireDefault(require("clsx"));
var _useZoomPercentage = _interopRequireDefault(require("../hooks/useZoomPercentage"));
var _camelCase = _interopRequireDefault(require("../poly/camelCase"));
var _styles2 = require("../utilities/styles");
const _excluded = ["children", "classes", "value", "onChange", "indicatorType", "indicatorColor", "variant", "scrollColor"];
const useClasses = (0, _styles.makeStyles)(({
  breakpoints,
  accessibility: {
    highContrast
  },
  palette,
  gutters
}) => ({
  root: {
    width: '100%'
  },
  rootScrollable: {
    '& .MuiTabScrollButton-root ~ .MuiTabs-scroller .MuiTabs-flexContainer': {
      justifyContent: 'flex-start !important'
    },
    [breakpoints.down('xs')]: {
      width: '100vw',
      overflow: 'visible',
      marginRight: `-${gutters.page.xs}`,
      marginLeft: `-${gutters.page.xs}`,
      '& .MuiTab-root:last-child': {
        marginRight: gutters.page.xs
      }
    }
  },
  flexContainer: {
    [breakpoints.up('md')]: {
      justifyContent: 'center'
    }
  },
  defaultFixed: {
    borderBottom: `1px solid ${palette.border.contrast}`
  },
  defaultIndicator: {
    height: '5px',
    [highContrast.mediaQuery('active')]: {
      display: 'none'
    }
  },
  defaultScrollable: {
    borderBottom: `1px solid ${palette.border.contrast}`,
    marginBottom: '0 !important',
    [breakpoints.down('xs')]: {
      display: 'flex'
    }
  },
  pillFixed: {
    borderBottom: 'none'
  },
  pillScrollable: {
    borderBottom: 'none',
    marginBottom: '0 !important',
    [breakpoints.down('xs')]: {
      display: 'flex'
    }
  },
  pillIndicator: {
    display: 'none'
  },
  tabScrollButton: {
    '& svg': {
      fontSize: '2.5em'
    },
    '&:hover, &:focus': {
      color: ({
        scrollColor
      }) => !scrollColor ? palette.primary.main : scrollColor,
      outline: `2px solid ${palette.border.contrast}`
    }
  },
  firstTab: {
    [breakpoints.down('xs')]: {
      marginLeft: gutters.page.xs,
      '& > button:first-child': {
        marginLeft: 0
      }
    }
  },
  lastTab: {
    [breakpoints.down('xs')]: {
      '& > button:last-child': {
        marginRight: 0
      }
    },
    '&::after': {
      position: 'absolute',
      content: '""',
      height: '100%',
      paddingRight: gutters.page.xs
    }
  }
}), {
  classNamePrefix: 'TCUITabs'
});
const convertChildren = (classes, children, onChange, value, variant, indicatorType, indicatorColor) => {
  const numChildren = _react.default.Children.count(children);
  return _react.default.Children.map(children, (child, childIndex) => {
    if (! /*#__PURE__*/_react.default.isValidElement(child)) {
      return null;
    }
    if (process.env.NODE_ENV !== 'production') {
      if (child.type === _react.default.Fragment) {
        console.error(['Material-UI: the Tabs component doesn\'t accept a Fragment as a child.', 'Consider providing an array instead.'].join('\n'));
      }
    }
    const isFirstTab = childIndex === 0;
    const isLastTab = childIndex === numChildren - 1;
    return /*#__PURE__*/_react.default.cloneElement(child, {
      onChange,
      indicatorColor,
      indicatorType,
      classes: (0, _styles2.mergeClasses)(child.props.classes, {
        root: (0, _clsx.default)({
          [classes.firstTab]: variant === 'scrollable' && isFirstTab,
          [classes.lastTab]: variant === 'scrollable' && isLastTab
        })
      }),
      fullWidth: variant === 'fullWidth',
      selected: (child.props.value || childIndex) === value
    });
  });
};
const Tabs = _ref => {
  let {
      children,
      classes,
      value,
      onChange,
      indicatorType,
      indicatorColor,
      variant,
      scrollColor
    } = _ref,
    rest = (0, _objectWithoutPropertiesLoose2.default)(_ref, _excluded);
  const theme = (0, _styles.useTheme)();
  const isMobile = (0, _useMediaQuery.default)(theme.breakpoints.down('xs'));
  const zoomPercentage = (0, _useZoomPercentage.default)();
  const _classes = useClasses({
    scrollColor
  });
  return /*#__PURE__*/_react.default.createElement(_core.Tabs, (0, _extends2.default)({
    scrollButtons: isMobile && zoomPercentage === 100 ? null : 'auto',
    variant: "scrollable"
  }, rest, {
    value: value,
    onChange: onChange,
    indicatorColor: indicatorColor,
    classes: (0, _styles2.mergeClasses)({
      root: (0, _clsx.default)(_classes.root, _classes[(0, _camelCase.default)([indicatorType, 'root'])], {
        [_classes.rootScrollable]: variant
      }),
      fixed: _classes[(0, _camelCase.default)([indicatorType, 'fixed'])],
      indicator: _classes[(0, _camelCase.default)([indicatorType, 'indicator'])],
      scrollable: _classes[(0, _camelCase.default)([indicatorType, 'scrollable'])],
      flexContainer: _classes.flexContainer
    }, classes),
    TabScrollButtonProps: {
      className: _classes.tabScrollButton,
      tabIndex: 0
    }
  }), convertChildren(_classes, children, onChange, value, variant || _variant, indicatorType, indicatorColor));
};
Tabs.propTypes = {
  children: _propTypes.default.node,
  classes: _propTypes.default.object,
  value: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.string]),
  variant: _propTypes.default.oneOf(['standard', 'scrollable', 'fullWidth']),
  onChange: _propTypes.default.func,
  indicatorType: _propTypes.default.oneOf(['default', 'pill']),
  indicatorColor: _propTypes.default.string,
  scrollColor: _propTypes.default.string
};
Tabs.defaultProps = {
  children: null,
  classes: {},
  value: null,
  onChange: () => null,
  indicatorColor: 'primary',
  indicatorType: 'default',
  variant: 'scrollable',
  scrollColor: null
};
var _default = exports.default = Tabs;